import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import baseUrl from "../api/utils";
import "../assets/css/invoicestyle.css";
import { getPermission } from "../utils/permissionUtils";
import MODULES from "../utils/modules";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const ViewSuppliers = () => {
  const token    = sessionStorage.getItem("token");
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const canCreate = getPermission(MODULES.PARTIES, "canCreate");
  const canEdit   = getPermission(MODULES.PARTIES, "canEdit");
  const canDelete   = getPermission(MODULES.PARTIES, "canDelete");
  const getViewPath = (txn) =>
  txn.billType === "ESTIMATE" ? `/addestimate/${txn.id}` : `/addsale/${txn.id}`;
  const getEditPath = (txn) =>
  txn.billType === "ESTIMATE" ? `/editestimate/${txn.id}` : `/editsale/${txn.id}`;


  const [suppliers,        setSuppliers]        = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [transactions,     setTransactions]     = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [txnLoading,       setTxnLoading]       = useState(false);
  const [searchQuery,      setSearchQuery]      = useState("");
  const [txnSearch,        setTxnSearch]        = useState("");
  const [showSearchBox,    setShowSearchBox]    = useState(false);
  const [openMenuId,       setOpenMenuId]       = useState(null);
  const [filterOption,     setFilterOption]     = useState("All");
  const [balanceMap,       setBalanceMap]       = useState({});
  const [sendingReminder, setSendingReminder] = useState(false);
  const [sendingInvoiceReminderId, setSendingInvoiceReminderId] = useState(null);
  const [sharingInvoiceId, setSharingInvoiceId] = useState(null);
  const [reminderSchedules, setReminderSchedules] = useState({});
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showBalanceOnly, setShowBalanceOnly] = useState(false);
  const filterMenuRef = useRef(null);

  // ── Pagination state (numbered pages, like 1 2 3 ... 11) ──
  const [page,       setPage]       = useState(0);   // 0-indexed internally
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;

  const addMenuRef = useRef(null);
  // key: partyId or "partyId_invoiceNumber" (for balance vs per-invoice), value: schedule object or null
  const tableMenuRef = useRef(null);

  useEffect(() => {
  const handler = (e) => {
    if (tableMenuRef.current && !tableMenuRef.current.contains(e.target))
      setOpenMenuId(null);
    if (filterMenuRef.current && !filterMenuRef.current.contains(e.target))
      setShowFilterMenu(false);
  };
  document.addEventListener("mousedown", handler);
  return () => document.removeEventListener("mousedown", handler);
}, []);

 

  // ── Debounced search — refetch page 1 whenever search text changes ──
 useEffect(() => {
  let cancelled = false;

  const timer = setTimeout(() => {
    fetchSuppliers(0, searchQuery, showBalanceOnly, () => cancelled);
  }, 400);

  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [searchQuery, showBalanceOnly]);

  useEffect(() => {
  const handleAddMenuClick = (e) => {
    if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
      setShowAddMenu(false);
    }
  };
  document.addEventListener("mousedown", handleAddMenuClick);
  return () => document.removeEventListener("mousedown", handleAddMenuClick);
}, []);

  useEffect(() => {
    if (suppliers.length > 0 && !selectedSupplier) {
      handleSupplierClick(suppliers[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers]);

  const fetchAllBalances = async (supplierList) => {
    const map = {};
    await Promise.all(
      supplierList.map(async (s) => {
        try {
          const res = await axios.get(`${baseUrl}/getPartyTransactions`, {
            headers: { Authorization: token },
            params: { partyName: s.name },
          });
          const data = Array.isArray(res.data) ? res.data : res.data?.content ?? [];
          const total = data.reduce((sum, t) => sum + (Number(t.balanceAmount) || 0), 0);
          map[s.id] = total;
        } catch {
          map[s.id] = 0;
        }
      })
    );
    // merge instead of overwrite, so balances from earlier pages aren't lost
    setBalanceMap((prev) => ({ ...prev, ...map }));
  };

  // ── Paginated + searchable supplier fetch (replaces list, never appends) ──
 const fetchSuppliers = async (pageNum = 0, search = "", hasBalance = false, isCancelled = () => false) => {
  try {
    setLoading(true);
    const res = await axios.get(`${baseUrl}/getSuppliers`, {
      headers: { Authorization: token },
      params: {
        page: pageNum,
        size: PAGE_SIZE,
        ...(search ? { search } : {}),
        ...(hasBalance ? { hasBalance: true } : {}),
      },
    });

    if (isCancelled()) return; // a newer request superseded this one — drop it

    const isPageResponse = res.data && Array.isArray(res.data.content);
    const newList = isPageResponse ? res.data.content : (Array.isArray(res.data) ? res.data : []);
    const newTotalPages = isPageResponse ? (res.data.totalPages ?? 1) : 1;
    const newTotalCount = isPageResponse ? (res.data.totalElements ?? newList.length) : newList.length;

    setTotalPages(newTotalPages);
    setTotalCount(newTotalCount);
    setPage(pageNum);
    setSuppliers(newList);
    fetchAllBalances(newList);
  } catch (err) {
    if (!isCancelled()) {
      console.error("Failed to fetch suppliers", err);
      setSuppliers([]);
    }
  } finally {
    if (!isCancelled()) setLoading(false);
  }
};

  // ── Clicking a page number ──
  const handlePageClick = (pageNum) => {
    if (pageNum < 0 || pageNum >= totalPages || pageNum === page) return;
    fetchSuppliers(pageNum, searchQuery);
  };

  // ── Builds the [1, 2, 3, "...", 11] style array of page numbers to show ──
  const getPageNumbers = () => {
    const current = page + 1; // 1-indexed for display
    const total = totalPages;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (current <= 3) return [1, 2, 3, "...", total];
    if (current >= total - 2) return [1, "...", total - 2, total - 1, total];
    return [1, "...", current, "...", total];
  };

  const fetchTransactions = async (partyName) => {
    if (!partyName) return;
    try {
      setTxnLoading(true);
      const res = await axios.get(`${baseUrl}/getPartyTransactions`, {
        headers: { Authorization: token },
        params: { partyName },
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.content ?? [];
      setTransactions(data);
    } catch (err) {
      console.error("Failed to fetch transactions", err);
      setTransactions([]);
    } finally {
      setTxnLoading(false);
    }
  };

  const handleDeleteTxn = async (id) => {
  const result = await MySwal.fire({
    title: "Delete Transaction?",
    text: "This action cannot be undone.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#e74c3c",
    cancelButtonColor: "#aaa",
    confirmButtonText: "Yes, Delete",
  });

  if (result.isConfirmed) {
    try {
      await axios.delete(`${baseUrl}/deleteBill/${id}`, {
        headers: { Authorization: token },
      });
      MySwal.fire("Deleted!", "Transaction deleted successfully.", "success");
      fetchTransactions(selectedSupplier.name);
      fetchAllBalances(suppliers);
    } catch (err) {
      MySwal.fire("Error", "Failed to delete transaction.", "error");
    }
  }
};


const handleSendBalanceReminder = async () => {
  if (!selectedSupplier) return;
  try {
    setSendingReminder(true);
    const res = await axios.post(
      `${baseUrl}/sendBalanceReminder/${selectedSupplier.id}`,
      {},
      { headers: { Authorization: token } }
    );
    MySwal.fire("Sent!", res.data || "Reminder sent successfully.", "success");
  } catch (err) {
    const msg = err.response?.data || "Failed to send reminder.";
    MySwal.fire("Error", msg, "error");
  } finally {
    setSendingReminder(false);
  }
};

// ═══════════════════════════════════════════════
// WHATSAPP BALANCE REMINDER — free wa.me link method,
// no backend call needed, opens WhatsApp with the
// message pre-filled for the user to send manually.
// ═══════════════════════════════════════════════
const handleSendWhatsAppReminder = () => {
  if (!selectedSupplier) return;

  if (!selectedSupplier.phone) {
    MySwal.fire("No Phone Number", "This supplier has no phone number saved.", "warning");
    return;
  }

  const message = `Hi ${selectedSupplier.name}, this is a reminder that you have an outstanding balance of ₹${totalBalance.toFixed(2)}. Please pay at your earliest convenience. Thank you!`;

  // Strip everything except digits (spaces, dashes, +, brackets etc.)
  const phone = selectedSupplier.phone.replace(/\D/g, "");

  // Prepend India country code (91). Adjust here if you support other countries.
  const url = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;

  window.open(url, "_blank");
};

const handleSendInvoiceReminder = async (txn) => {
  if (!selectedSupplier) return;
  try {
    setSendingInvoiceReminderId(txn.id);
    const res = await axios.post(
      `${baseUrl}/sendInvoiceReminder/${selectedSupplier.id}/${txn.invoiceNumber}`,
      {},
      { headers: { Authorization: token } }
    );
    MySwal.fire("Sent!", res.data || "Reminder sent successfully.", "success");
    setOpenMenuId(null);
  } catch (err) {
    const msg = err.response?.data || "Failed to send reminder.";
    MySwal.fire("Error", msg, "error");
  } finally {
    setSendingInvoiceReminderId(null);
  }
};

// ═══════════════════════════════════════════════
// WHATSAPP INVOICE REMINDER — same free wa.me method,
// used per-invoice from the row menu.
// ═══════════════════════════════════════════════
const handleSendInvoiceWhatsAppReminder = (txn) => {
  if (!selectedSupplier) return;

  if (!selectedSupplier.phone) {
    MySwal.fire("No Phone Number", "This supplier has no phone number saved.", "warning");
    return;
  }

  const message = `Hi ${selectedSupplier.name}, invoice #${txn.invoiceNumber} has a pending balance of ₹${Number(txn.balanceAmount || 0).toFixed(2)}. Kindly clear it soon. Thank you!`;

  const phone = selectedSupplier.phone.replace(/\D/g, "");
  const url = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;

  window.open(url, "_blank");
  setOpenMenuId(null);
};

// ═══════════════════════════════════════════════
// SHARE INVOICE — sends the public, no-login invoice link
// over WhatsApp so the supplier can open/download it themselves.
// Fetches (or lazily creates) an unguessable share token from the backend
// first — the public link is built from that token, NEVER from txn.id,
// so invoices can't be enumerated by walking sequential ids.
// ═══════════════════════════════════════════════
const handleShareInvoice = async (txn) => {
  if (!selectedSupplier) return;
  try {
    setSharingInvoiceId(txn.id);
    const res = await axios.get(`${baseUrl}/getOrCreateShareToken/${txn.id}`, {
      headers: { Authorization: token },
    });
    const shareToken = res.data.shareToken;

    // NOTE: adjust this path if the public route registered in App.js differs
    const publicUrl = `${window.location.origin}/public-invoice/${shareToken}`;
    const message = `Hi ${selectedSupplier.name}, here is your invoice #${txn.invoiceNumber}. You can view/download it here: ${publicUrl}`;

    if (selectedSupplier.phone) {
      const phone = selectedSupplier.phone.replace(/\D/g, "");
      const url = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    } else {
      // No phone saved on this supplier — open WhatsApp's generic share screen
      // so the user can pick a contact manually instead of failing silently.
      const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    }
  } catch (err) {
    MySwal.fire("Error", "Failed to generate share link.", "error");
  } finally {
    setSharingInvoiceId(null);
    setOpenMenuId(null);
  }
};

const fetchReminderSchedules = async (partyId) => {
  try {
    const res = await axios.get(`${baseUrl}/getReminderSchedules/${partyId}`, {
      headers: { Authorization: token },
    });
    const list = Array.isArray(res.data) ? res.data : [];
    const map = {};
    list.forEach((s) => {
      const key = s.invoiceNumber ? `${s.partyId}_${s.invoiceNumber}` : `${s.partyId}_balance`;
      if (s.isActive) map[key] = s;
    });
    setReminderSchedules(map);
  } catch (err) {
    console.error("Failed to fetch reminder schedules", err);
  }
};


const openScheduleModal = async (invoiceNumber = null) => {
  if (!selectedSupplier) return;

  const key = invoiceNumber ? `${selectedSupplier.id}_${invoiceNumber}` : `${selectedSupplier.id}_balance`;
  const existing = reminderSchedules[key];

  if (existing) {
    // Already active → offer to stop
    const result = await MySwal.fire({
      title: "Reminder Active",
      html: `This reminder is set for <b>${String(existing.sendHour).padStart(2, "0")}:${String(existing.sendMinute).padStart(2, "0")}</b> daily.<br/>Do you want to stop it?`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Stop Reminder",
      confirmButtonColor: "#e74c3c",
      cancelButtonText: "Close",
    });
    if (result.isConfirmed) {
      try {
        await axios.post(`${baseUrl}/stopReminder/${existing.id}`, {}, {
          headers: { Authorization: token },
        });
        MySwal.fire("Stopped", "Reminder schedule stopped.", "success");
        fetchReminderSchedules(selectedSupplier.id);
      } catch (err) {
        MySwal.fire("Error", err.response?.data || "Failed to stop reminder.", "error");
      }
    }
    return;
  }

  // Not active → ask for time and start
  const { value: time } = await MySwal.fire({
    title: invoiceNumber ? `Schedule Reminder — Invoice #${invoiceNumber}` : "Schedule Balance Reminder",
    html: `Reminder will be sent automatically every day at the selected time, until the ${invoiceNumber ? "invoice" : "balance"} is fully paid or you stop it.`,
    input: "time",
    inputValue: "10:00",
    showCancelButton: true,
    confirmButtonText: "Start Reminder",
    confirmButtonColor: "#27ae60",
    inputValidator: (value) => {
      if (!value) return "Please select a time";
    },
  });

  if (time) {
    const [sendHour, sendMinute] = time.split(":").map(Number);
    try {
      const params = new URLSearchParams({
        partyId: selectedSupplier.id,
        sendHour,
        sendMinute,
      });
      if (invoiceNumber) params.append("invoiceNumber", invoiceNumber);

      await axios.post(`${baseUrl}/startReminder?${params.toString()}`, {}, {
        headers: { Authorization: token },
      });
      MySwal.fire("Scheduled!", `Reminder will be sent daily at ${time}.`, "success");
      fetchReminderSchedules(selectedSupplier.id);
    } catch (err) {
      MySwal.fire("Error", err.response?.data || "Failed to start reminder.", "error");
    }
  }
};

  const handleSupplierClick = (supplier) => {
    setSelectedSupplier(supplier);
    setTransactions([]);
    fetchTransactions(supplier.name);
     fetchReminderSchedules(supplier.id);
  };

  // Suppliers already come filtered from the backend (search param),
  // so no client-side name filter needed anymore.
const filteredSuppliers = suppliers.filter((s) =>
  showBalanceOnly ? (balanceMap[s.id] ?? 0) > 0 : true
);
  const filteredTxns = transactions.filter((t) => {
    if (filterOption === "Paid")   return !t.balanceAmount || Number(t.balanceAmount) <= 0;
    if (filterOption === "Unpaid") return t.balanceAmount  && Number(t.balanceAmount) > 0;
    const q = txnSearch.toLowerCase();
    return !q || t.billType?.toLowerCase().includes(q) || t.invoiceNumber?.toString().includes(q);
  }).filter((t) => {
    if (!txnSearch) return true;
    const q = txnSearch.toLowerCase();
    return t.billType?.toLowerCase().includes(q) || t.invoiceNumber?.toString().includes(q);
  });

  const totalBalance    = transactions.reduce((sum, t) => sum + (Number(t.balanceAmount) || 0), 0);
  const totalGrandTotal = transactions.reduce((sum, t) => sum + (Number(t.grandTotal)    || 0), 0);

return (
    <div>
      <div className="invoice-card-wrapper vt-card-wrapper">
        <div>
          <div className="card invoice-card">
            <div className="card-body p-0">
              <div className="invoice-panel-layout">

                {/* ══ LEFT PANEL ══ */}
                <div className="invoice-left-panel">
                  <div className="invoice-left-panel-header">
                    <div className="vt-left-header-inner">
                      <div onClick={() => navigate(-1)} style={{ cursor: "pointer", fontSize: "16px", color: "#333" }}>
                        <i className="fa-solid fa-arrow-left"></i>
                      </div>
                      <h6 className="invoice-left-panel-title">Suppliers</h6>
                      <span className="invoice-count-badge">{totalCount}</span>
                    </div>
                  </div>

                  <div className="invoice-search-box">
                    <div className="input-group input-group-sm">
                      <div className="input-group-prepend">
                        <span className="input-group-text bg-white border-right-0">
                          <i className="fa-solid fa-magnifying-glass" style={{ color: "#aaa" }}></i>
                        </span>
                      </div>
                      <input type="text" className="form-control border-left-0"
                        placeholder="Search supplier name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ boxShadow: "none" }} />
                    </div>
                  </div>

                  <div className="invoice-column-headers">
                   <span className="invoice-column-header-text" style={{ flex: 1 }}>
                    SUPPLIERS NAME
                    <span className="vc-row-menu-wrap" ref={filterMenuRef} style={{ position: "relative", display: "inline-block" }}>
                      <i
                        className="fa-solid fa-filter ml-1 vt-filter-icon"
                        onClick={() => setShowFilterMenu((prev) => !prev)}
                        style={{
                          cursor: "pointer",
                          color: showBalanceOnly ? "#e74c3c" : undefined,
                        }}
                      ></i>

                      {showFilterMenu && (
                        <div className="vc-row-dropdown" style={{ top: "110%", left: 0 }}>
                          <button
                            onClick={() => {
                              setShowBalanceOnly(false);
                              setShowFilterMenu(false);
                            }}
                          >
                            <i className="feather icon-list"></i> All Suppliers
                          </button>
                          <button
                            onClick={() => {
                              setShowBalanceOnly(true);
                              setShowFilterMenu(false);
                            }}
                          >
                            <i className="fa-solid fa-indian-rupee-sign"></i> Balance Due
                          </button>
                        </div>
                      )}
                    </span>
                  </span>
                    <span className="invoice-column-header-text">BALANCE</span>
                  </div>

                  <div
                      className="invoice-list-scroll"
                      style={{ maxHeight: 400, overflowY: "auto" }}   // ~10 rows visible, rest scrolls
                    >
                    {loading ? (
                      <div className="text-center p-4">
                        <div className="spinner-border spinner-border-sm text-danger"></div>
                      </div>
                    ) : filteredSuppliers.length === 0 ? (
                      <div className="text-center p-4" style={{ color: "#aaa", fontSize: 13 }}>
                        No suppliers found
                      </div>
                    ) : (
                      filteredSuppliers.map((supplier) => {
                        const isSelected = selectedSupplier?.id === supplier.id;
                        const displayBalance = isSelected
                          ? totalBalance
                          : (balanceMap[supplier.id] ?? 0);
                        return (
                          <div key={supplier.id}
                            className={`invoice-list-item ${isSelected ? "selected" : ""}`}
                            onClick={() => handleSupplierClick(supplier)}>
                            <span className={`invoice-list-item-name ${isSelected ? "selected" : ""}`}>
                              {supplier.name}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: displayBalance > 0 ? "#e74c3c" : "#27ae60" }}>
                              {Number(displayBalance).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* ── Fixed numbered pagination bar — sits below the scrollable list, card height never changes ── */}
                  {!loading && totalCount > 0 && (
                    <div className="invoice-pagination-bar" style={{ padding: "8px 12px", borderTop: "1px solid #eee" }}>
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          style={{ padding: "2px 8px", fontSize: 12 }}
                          disabled={page === 0}
                          onClick={() => handlePageClick(page - 1)}
                        >
                          «
                        </button>

                        {getPageNumbers().map((p, idx) =>
                          p === "..." ? (
                            <span key={`ellipsis-${idx}`} style={{ padding: "0 4px", fontSize: 12, color: "#aaa" }}>...</span>
                          ) : (
                            <button
                              key={p}
                              className={`btn btn-sm ${p === page + 1 ? "btn-danger" : "btn-outline-secondary"}`}
                              style={{ padding: "2px 9px", fontSize: 12, minWidth: 28 }}
                              onClick={() => handlePageClick(p - 1)}
                            >
                              {p}
                            </button>
                          )
                        )}

                        <button
                          className="btn btn-sm btn-outline-secondary"
                          style={{ padding: "2px 8px", fontSize: 12 }}
                          disabled={page + 1 >= totalPages}
                          onClick={() => handlePageClick(page + 1)}
                        >
                          »
                        </button>
                      </div>
                      <div style={{ textAlign: "center", fontSize: 11, color: "#999", marginTop: 4 }}>
                        Showing {page * PAGE_SIZE + 1} to {Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount} suppliers
                      </div>
                    </div>
                  )}
                </div>

                {/* ══ RIGHT PANEL ══ */}
                <div className="invoice-right-panel">
                  <div className="invoice-right-panel-header">
                    <div className="vt-right-header-left">
                      <h6 className="invoice-right-panel-title">
                        {selectedSupplier ? selectedSupplier.name : "Suppliers"}
                      </h6>
                     {selectedSupplier && canEdit && (
                        <i className="fa-solid fa-pen vt-edit-pen-icon"
                          style={{ cursor: "pointer", marginLeft: 8 }}
                          onClick={() => navigate(`/editSupplier/${selectedSupplier.id}`)}>
                        </i>
                      )}
                    </div>

                    <div className="vt-right-header-right" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <select className="form-control form-control-sm invoice-filter-select"
                        value={filterOption}
                        onChange={(e) => setFilterOption(e.target.value)}
                        style={{ width: "auto", fontSize: 12 }}>
                        <option value="All">All</option>
                        <option value="Paid">Paid</option>
                        <option value="Unpaid">Unpaid</option>
                      </select>

                      {canCreate && (
                        <div className="vc-add-item-dropdown-wrap" ref={addMenuRef} style={{ position: "relative" }}>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => setShowAddMenu((prev) => !prev)}
                          >
                            <i className="feather icon-plus" style={{ marginRight: 5 }}></i>
                            Add Supplier
                            <i className="feather icon-chevron-down" style={{ marginLeft: 5, fontSize: "0.72rem" }}></i>
                          </button>

                          {showAddMenu && (
                            <div className="vc-row-dropdown" style={{ top: "110%", right: 0, left: "auto" }}>
                              <button onClick={() => {
                                setShowAddMenu(false);
                                navigate("/addSupplier");
                              }}>
                                <i className="feather icon-plus-circle"></i> Add Single Supplier
                              </button>
                              <button onClick={() => {
                                setShowAddMenu(false);
                                navigate("/bulkAddSupplier");
                              }}>
                                <i className="feather icon-upload"></i> Bulk Add Supplier
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedSupplier ? (
                    <>
                      {/* ── Summary Cards ── */}
                      <div style={{ display: "flex", gap: 12, padding: "12px 16px 0 16px" }}>
                        <div style={{ flex: 1, background: "#f0f7ff", borderRadius: 8, padding: "10px 14px", borderLeft: "3px solid #1a73e8" }}>
                          <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>TOTAL PURCHASES</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#222" }}>
                            <i className="fa-solid fa-indian-rupee-sign" style={{ fontSize: "0.75rem", marginRight: 2 }}></i>
                            {Number(totalGrandTotal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </div>
                        </div>
                        <div style={{ flex: 1, background: totalBalance > 0 ? "#fff0f0" : "#f0fdf4", borderRadius: 8, padding: "10px 14px", borderLeft: `3px solid ${totalBalance > 0 ? "#e74c3c" : "#27ae60"}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>OUTSTANDING BALANCE</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: totalBalance > 0 ? "#e74c3c" : "#27ae60" }}>
                            <i className="fa-solid fa-indian-rupee-sign" style={{ fontSize: "0.75rem", marginRight: 2 }}></i>
                            {Number(totalBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </div>
                        </div>
                       {totalBalance > 0 && (
                          <div style={{ display: "flex", gap: 10 }}>
                            <i
                              className={`fa-solid ${sendingReminder ? "fa-spinner fa-spin" : "fa-envelope"}`}
                              title="Send reminder now"
                              style={{ cursor: sendingReminder ? "default" : "pointer", color: "#e74c3c", fontSize: 16 }}
                              onClick={() => !sendingReminder && handleSendBalanceReminder()}
                            ></i>
                            <i
                              className="fa-brands fa-whatsapp"
                              title="Send WhatsApp reminder"
                              style={{ cursor: "pointer", color: "#25D366", fontSize: 16 }}
                              onClick={handleSendWhatsAppReminder}
                            ></i>
                            <i
                              className="fa-solid fa-clock"
                              title={reminderSchedules[`${selectedSupplier.id}_balance`] ? "Reminder scheduled — click to stop" : "Schedule daily reminder"}
                              style={{
                                cursor: "pointer",
                                color: reminderSchedules[`${selectedSupplier.id}_balance`] ? "#27ae60" : "#888",
                                fontSize: 16,
                              }}
                              onClick={() => openScheduleModal(null)}
                            ></i>
                          </div>
                        )}
                      </div>
                        <div style={{ flex: 1, background: "#f5f5f5", borderRadius: 8, padding: "10px 14px", borderLeft: "3px solid #aaa" }}>
                          <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>TRANSACTIONS</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#333" }}>{transactions.length}</div>
                        </div>
                      </div>

                      {/* ── Transactions Table ── */}
                      <div className="invoice-details-section">
                        <div className="invoice-details-header">
                          <h6 className="invoice-details-title">Transactions Details</h6>
                          <div className="vt-details-icons-row">
                            <i className="fa-solid fa-magnifying-glass invoice-icon" style={{ cursor: "pointer" }}
                              onClick={() => setShowSearchBox(!showSearchBox)}></i>
                            {showSearchBox && (
                              <input type="text" placeholder="Search..."
                                className="form-control form-control-sm vt-search-toggle-input"
                                value={txnSearch}
                                onChange={(e) => setTxnSearch(e.target.value)} />
                            )}
                            <i className="fa-solid fa-print invoice-icon" style={{ cursor: "pointer" }}></i>
                            <i className="fa-solid fa-rotate-right invoice-icon" style={{ cursor: "pointer" }}
                              onClick={() => fetchTransactions(selectedSupplier.name)}></i>
                          </div>
                        </div>

                        <div style={{ overflowX: "auto", overflowY: "auto", width: "100%", maxHeight: "480px" }}>
                          <table className="table table-hover table-bordered table-sm invoice-table">
                            <thead style={{ background: "#222", color: "#fff", position: "sticky", top: 0, zIndex: 1 }}>
                              <tr>
                                <th>Type</th>
                                <th>Invoice Number</th>
                                <th>Date</th>
                                <th>Total Amount</th>
                                <th>Balance / Unused</th>
                                <th>Status</th>
                                <th style={{ width: 18 }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {txnLoading && (
                                <tr><td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                                  <div className="spinner-border spinner-border-sm text-danger"></div>
                                </td></tr>
                              )}
                              {!txnLoading && filteredTxns.length === 0 && (
                                <tr><td colSpan={7} style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                                  No transactions found
                                </td></tr>
                              )}
                              {!txnLoading && filteredTxns.map((txn, idx) => {
                                const isPaid = !txn.balanceAmount || Number(txn.balanceAmount) <= 0;
                                return (
                                  <tr key={idx}>
                                    <td>
                                      <span style={{
                                        padding: "2px 8px", borderRadius: 10, fontSize: "11px", fontWeight: 600,
                                        background: txn.billType === "SALE" ? "#e8f4fd" : txn.billType === "POS" ? "#fef3e2" : txn.billType === "ESTIMATE" ? "#f0fdf4" : "#f5f5f5",
                                        color:     txn.billType === "SALE" ? "#1a73e8" : txn.billType === "POS" ? "#e65100" : txn.billType === "ESTIMATE" ? "#137333" : "#555",
                                      }}>{txn.billType || "—"}</span>
                                    </td>
                                    <td>{txn.invoiceNumber || "—"}</td>
                                    <td>{txn.invoiceDate ? new Date(txn.invoiceDate).toLocaleDateString("en-GB") : "—"}</td>
                                    <td style={{ fontWeight: 600 }}>
                                      {txn.grandTotal != null ? <>
                                        <i className="fa-solid fa-indian-rupee-sign" style={{ fontSize: "0.7rem", marginRight: 2 }}></i>
                                        {Number(txn.grandTotal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                      </> : "—"}
                                    </td>
                                    <td style={{ color: isPaid ? "#27ae60" : "#e74c3c", fontWeight: 500 }}>
                                      <i className="fa-solid fa-indian-rupee-sign" style={{ fontSize: "0.7rem", marginRight: 2 }}></i>
                                      {Number(txn.balanceAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                    </td>
                                    <td>
                                      <span style={{
                                        padding: "2px 10px", borderRadius: 12, fontSize: "11px", fontWeight: 600,
                                        background: isPaid ? "#e6f4ea" : "#fdecea",
                                        color:      isPaid ? "#137333" : "#d93025",
                                      }}>{txn.billType === "ESTIMATE" ? "—" : (isPaid ? "Paid" : "Unpaid")}</span>
                                    </td>
                                    <td style={{ position: "relative" }} ref={openMenuId === idx ? tableMenuRef : null}>
                                      <button className="vc-row-menu-btn"
                                        onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === idx ? null : idx); }}>
                                        <i className="feather icon-more-vertical"></i>
                                      </button>
                                     {openMenuId === idx && (
                                          <div className="vc-row-dropdown vt-row-dropdown" style={{ right: 0, left: "auto", zIndex: 200 }}>
                                        <button onClick={() => navigate(getViewPath(txn))}>
                                          <i className="feather icon-eye"></i> View
                                        </button>
                                        <button
                                          onClick={() => handleShareInvoice(txn)}
                                          disabled={sharingInvoiceId === txn.id}
                                        >
                                          <i className={sharingInvoiceId === txn.id ? "feather icon-loader" : "fa-brands fa-whatsapp"} style={{ color: "#25D366" }}></i>
                                          {sharingInvoiceId === txn.id ? " Generating link..." : " Share Invoice"}
                                        </button>
                                        <button><i className="feather icon-printer"></i> Print</button>
                                        {canEdit && (
                                          <button onClick={() => navigate(getEditPath(txn))}>
                                            <i className="feather icon-edit"></i> Edit
                                          </button>
                                        )}
                                        {!isPaid && txn.billType !== "ESTIMATE" && (
                                          <button
                                            onClick={() => handleSendInvoiceReminder(txn)}
                                            disabled={sendingInvoiceReminderId === txn.id}
                                          >
                                            <i className={`feather ${sendingInvoiceReminderId === txn.id ? "icon-loader" : "icon-mail"}`}></i>
                                            {sendingInvoiceReminderId === txn.id ? " Sending..." : " Send Reminder"}
                                          </button>
                                        )}
                                        {!isPaid && txn.billType !== "ESTIMATE" && (
                                          <button onClick={() => handleSendInvoiceWhatsAppReminder(txn)}>
                                            <i className="fa-brands fa-whatsapp" style={{ color: "#25D366" }}></i> Send WhatsApp Reminder
                                          </button>
                                        )}
                                        {canDelete && (
                                          <button onClick={() => handleDeleteTxn(txn.id)}>
                                            <i className="feather icon-trash-2"></i> Delete
                                          </button>
                                        )}
                                        {!isPaid && txn.billType !== "ESTIMATE" && (
                                        <button onClick={() => openScheduleModal(txn.invoiceNumber)}>
                                          <i className={`fa-solid fa-clock`} style={{
                                            color: reminderSchedules[`${selectedSupplier.id}_${txn.invoiceNumber}`] ? "#27ae60" : "inherit"
                                          }}></i>
                                          {reminderSchedules[`${selectedSupplier.id}_${txn.invoiceNumber}`] ? " Reminder Active" : " Schedule Reminder"}
                                        </button>
                                      )}
                                      </div>
                                        )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            {!txnLoading && filteredTxns.length > 0 && (
                              <tfoot>
                                <tr style={{ background: "#f5f5f5", fontWeight: 700 }}>
                                  <td colSpan={3} style={{ padding: "8px 12px", fontSize: 12 }}>
                                    Total ({filteredTxns.length} transactions)
                                  </td>
                                  <td style={{ padding: "8px 12px", fontSize: 13 }}>
                                    <i className="fa-solid fa-indian-rupee-sign" style={{ fontSize: "0.7rem", marginRight: 2 }}></i>
                                    {filteredTxns.reduce((s, t) => s + (Number(t.grandTotal) || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                  </td>
                                  <td style={{ padding: "8px 12px", fontSize: 13, color: totalBalance > 0 ? "#e74c3c" : "#27ae60" }}>
                                    <i className="fa-solid fa-indian-rupee-sign" style={{ fontSize: "0.7rem", marginRight: 2 }}></i>
                                    {filteredTxns.reduce((s, t) => s + (Number(t.balanceAmount) || 0), 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                  </td>
                                  <td colSpan={2}></td>
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="invoice-empty-state">
                      <i className="fa-solid fa-handshake"></i>
                      <p>Select a supplier to view details</p>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewSuppliers;